<template>
  <nav class="navbar navbar-expand-lg bg-light navbar-custom">
    <div class="container-fluid position-relative">
      <!-- 左：Logo -->
      <router-link to="/" class="navbar-brand d-flex align-items-center me-3" @click="closeMenu">
        <img src="@/assets/logo.png" alt="Logo" class="logo-img">
      </router-link>

      <!-- 手機版漢堡選單按鈕 -->
      <button 
        class="navbar-toggler" 
        type="button" 
        data-bs-toggle="collapse" 
        data-bs-target="#navbarContent" 
        aria-controls="navbarContent" 
        aria-expanded="false" 
        aria-label="Toggle navigation"
      >
        <span class="navbar-toggler-icon"></span>
      </button>

      <!-- 可折疊內容 (主選單 + 語系) -->
      <div class="collapse navbar-collapse" id="navbarContent" ref="navbarContentRef">
        <!-- 中：主選單 -->
        <!-- 桌面版使用絕對定位居中 -->
        <ul class="navbar-nav nav-menu mb-2 mb-lg-0">
          <li class="nav-item nav-item-custom">
            <router-link class="nav-link nav-link-custom" to="/about" @click="closeMenu">{{ $t('nav.about') }}</router-link>
          </li>
          <li class="nav-item nav-item-custom">
            <router-link class="nav-link nav-link-custom" to="/food-machine" @click="closeMenu">{{ $t('nav.foodMachine') }}</router-link>
          </li>
          <li class="nav-item nav-item-custom">
            <router-link class="nav-link nav-link-custom" to="/web-design" @click="closeMenu">{{ $t('nav.webDesign') }}</router-link>
          </li>
          <li class="nav-item nav-item-custom">
            <router-link class="nav-link nav-link-custom" to="/introduce" @click="closeMenu">{{ $t('nav.products') }}</router-link>
          </li>
          <li class="nav-item nav-item-custom">
            <router-link class="nav-link nav-link-custom" to="/contact" @click="closeMenu">{{ $t('nav.contact') }}</router-link>
          </li>
        </ul>

        <!-- 右：語系切換 -->
        <ul class="navbar-nav ms-auto mb-2 mb-lg-0">
          <!-- 使用 Vue ref 控制 Dropdown，不依賴 Bootstrap JS -->
          <li class="nav-item dropdown" ref="langDropdownRef">
            <button
              class="nav-link dropdown-toggle d-flex align-items-center btn btn-link lang-btn border-0 bg-transparent"
              type="button"
              :class="{ show: isDropdownOpen }"
              @click.stop="toggleDropdown"
              :aria-expanded="isDropdownOpen"
            >
              <img :src="currentFlag" alt="flag" class="me-2" style="width: 20px; height: 20px; object-fit: cover;">
              <span>{{ currentLabel }}</span>
            </button>

            <!-- 加上 :class="{ show: isDropdownOpen }" 控制顯示 -->
            <ul class="dropdown-menu dropdown-menu-end" :class="{ show: isDropdownOpen }">
              <li>
                <button class="dropdown-item d-flex align-items-center" type="button" @click="changeLocale('zh-TW')">
                  <img src="@/assets/flags/tw.svg" alt="TW" class="me-2" style="width: 20px; height: 20px;">
                  <span>{{ $t('lang.zhTW') }}</span>
                </button>
              </li>
              <li>
                <button class="dropdown-item d-flex align-items-center" type="button" @click="changeLocale('en')">
                  <img src="@/assets/flags/us.svg" alt="US" class="me-2" style="width: 20px; height: 20px;">
                  <span>{{ $t('lang.en') }}</span>
                </button>
              </li>
            </ul>
          </li>
        </ul>
      </div>
    </div>
  </nav>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { setLocale } from '@/i18n'
import { Collapse } from 'bootstrap'

const { locale, t } = useI18n()
const navbarContentRef = ref(null)

/* 手動控制語系下拉選單狀態 */
const isDropdownOpen = ref(false)
const langDropdownRef = ref(null)

const toggleDropdown = () => {
  isDropdownOpen.value = !isDropdownOpen.value
}

// 點擊外部關閉下拉選單
const closeDropdownOnClickOutside = (event) => {
  if (isDropdownOpen.value && langDropdownRef.value && !langDropdownRef.value.contains(event.target)) {
    isDropdownOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', closeDropdownOnClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', closeDropdownOnClickOutside)
})

const currentFlag = computed(() => {
  return locale.value === 'zh-TW'
    ? new URL('@/assets/flags/tw.svg', import.meta.url).href
    : new URL('@/assets/flags/us.svg', import.meta.url).href
})

const currentLabel = computed(() => {
  return locale.value === 'zh-TW' ? t('lang.zhTW') : t('lang.en')
})

function changeLocale(l) {
  if (locale.value !== l) setLocale(l)
  isDropdownOpen.value = false // 選擇後關閉下拉選單
  closeMenu() // 關閉漢堡選單
}

function closeMenu() {
  if (navbarContentRef.value && navbarContentRef.value.classList.contains('show')) {
    const bsCollapse = Collapse.getOrCreateInstance(navbarContentRef.value)
    bsCollapse.hide()
  }
}
</script>

<style scoped>
.navbar-custom {
  background-color: #ffffff !important;
  background-image: none !important;
  border-bottom: 5px solid #D7ECFF;
  box-sizing: border-box;
  z-index: 1000;
  min-height: 120px;
}

.logo-img {
  height: 100px;
  transition: height 0.3s ease;
}

.nav-link-custom {
  font-size: 1.6rem;
  padding: 0.5rem 0;
  white-space: nowrap;
  position: relative;
  transition: color .2s ease, box-shadow .18s ease;
  color: #333;
}

.nav-link-custom::after {
  content: "";
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 0;
  width: 0;
  height: 4px;
  background: rgba(0,0,0,0.1);
  border-radius: 4px;
  transition: width .22s cubic-bezier(.2,.8,.2,1);
}

.nav-link-custom:hover::after,
.nav-link-custom:focus::after {
  width: 80%;
}

.nav-link-custom:hover,
.nav-link-custom:focus {
  color: #000 !important;
}

/* Lang btn styling */
.lang-btn {
  font-size: 1rem;
  color: #333;
  text-decoration: none;
  cursor: pointer;
  outline: none;
}
.lang-btn:focus {
  box-shadow: none;
}

/* 桌面版 (Large screens) */
@media (min-width: 992px) {
  .navbar-collapse {
    position: static;
  }
  
  .nav-menu {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 30px;
    z-index: 10;
  }

  .navbar-nav.ms-auto {
    position: relative;
    z-index: 20;
  }
}

/* 平板過渡調整 */
@media (max-width: 1200px) and (min-width: 992px) {
  .nav-link-custom {
    font-size: 1.4rem;
  }
  .nav-menu {
    gap: 20px;
  }
}

/* 手機版 / 漢堡選單模式 (lg 以下) */
@media (max-width: 991px) {
  .navbar-custom {
    min-height: auto;
    padding: 10px 0;
  }

  .logo-img {
    height: 60px;
  }

  .nav-menu {
    flex-direction: column;
    gap: 10px;
    margin-top: 20px;
    margin-bottom: 20px;
    text-align: center;
    width: 100%;
    position: static;
    transform: none;
  }

  .nav-item-custom {
    width: 100%;
  }

  .nav-link-custom {
    font-size: 1.2rem;
    padding: 10px 0;
    display: block;
  }
  
  .navbar-nav.ms-auto {
    align-items: center;
    justify-content: center;
    width: 100%;
    margin-bottom: 20px;
  }
}
</style>